import { type HttpClientContract } from "@features/drivers/domain";
import cache, { CacheDriverInterface } from "@taager/cache";
import { GenericObject } from "@taager/reinforcements";
import { getRuntimeCache, getSessionCache, http } from "../../../drivers/data";
import { MockupAdapter } from "../../../drivers/data/mockup";

type CacheOptions = {
  key: string;
  driver?: "default" | "runtime" | "session" | "storage";
  ttl?: number;
};

export abstract class BaseRepository {
  /**
   * If the mockup options property is present and `adapter` property is called, then we will use the mockup adapter
   * otherwise, we will use the real http adapter
   */
  protected mockupOptions?: {
    data: any;
    delay?: number;
  };

  /**
   * Pending cache requests
   *
   * The major reason for this is to avoid race condition when the same cache key is requested multiple times
   * This will enhance performance dramatically to avoid multiple in parallel API calls
   */
  protected pendingCacheRequests: Record<string, Promise<any>> = {};

  /**
   * Get http service instance
   */
  protected get http(): HttpClientContract {
    return http;
  }

  /**
   * Cache the result of the given callback function
   * If the cache is available, it will return the cached data
   * If first argument is a string, then it will be treated as cache key with indefinite ttl
   * If first argument is an array, it should have first value as cache key and second value as ttl
   */

  protected async cacheable<T>(
    key: string | CacheOptions,
    callback: () => Promise<T>,
  ): Promise<T> {
    const options: CacheOptions =
      typeof key === "string" ? { key, driver: "runtime" } : key;

    const cache = this.getCacheDriver(options.driver);

    // This is important to properly cache data based on country and locale
    // options.key = this.cacheKey(options.key, {
    //   ctry: country.code,
    //   lng: getCurrentLocaleCode(),
    // });

    const cachedData = cache.get(options.key);

    if (cachedData) {
      return cachedData;
    }

    if (this.pendingCacheRequests[options.key] !== undefined) {
      return await this.pendingCacheRequests[options.key];
    }

    const promise = callback();

    this.pendingCacheRequests[options.key] = promise;

    const data = await promise;

    delete this.pendingCacheRequests[options.key];

    cache.set(options.key, data, options.ttl);

    return data;
  }

  /**
   * Generate cache key
   */
  protected cacheKey(key: string, params?: GenericObject) {
    return `${key}-${JSON.stringify(params || {})}`;
  }

  /**
   * Cache the given data in runtime cache
   */
  protected async runtimeCacheable<T>(
    key: string,
    callback: () => Promise<T>,
  ): Promise<T> {
    return this.cacheable({ key, driver: "runtime" }, callback);
  }

  /**
   * Cache the given data in session cache
   */
  protected async sessionCacheable<T>(
    key: string,
    callback: () => Promise<T>,
  ): Promise<T> {
    return this.cacheable({ key, driver: "session" }, callback);
  }

  /**
   * Cache the given data in local storage cache
   */
  protected async localCacheable<T>(
    key: string,
    callback: () => Promise<T>,
  ): Promise<T> {
    return this.cacheable({ key, driver: "storage" }, callback);
  }

  /**
   * Get cache driver
   */
  protected getCacheDriver(
    driver?: CacheOptions["driver"],
  ): CacheDriverInterface {
    switch (driver) {
      case "runtime":
        return getRuntimeCache();
      case "session":
        return getSessionCache();
      case "storage":
      default:
        return cache;
    }
  }

  /**
   * Get proper adapter
   */
  protected get adapter(): HttpClientContract {
    if (this.mockupOptions) return this.mockup;

    return this.http;
  }

  /**
   * Get mockup adapter
   */
  protected get mockup(): HttpClientContract {
    return new MockupAdapter(
      this.mockupOptions?.data,
      this.mockupOptions?.delay,
    );
  }

  protected async mock<Data>({
    data,
    callback,
    enabled,
    delay = 1000,
  }: {
    data: Data;
    callback: () => Promise<Data>;
    enabled: boolean;
    delay?: number;
  }): Promise<Data> {
    if (!enabled) {
      return callback();
    }

    return new Promise(resolve => {
      setTimeout(() => {
        resolve(data);
      }, delay);
    });
  }
}
